import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import { ScrollArea } from '../../components/ui/scroll-area';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from '../../components/ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../../components/ui/select';
import { toast } from 'sonner';
import {
  ArrowLeft, Settings, CheckCircle2, XCircle, Loader2, RefreshCw,
  FolderGit2, Shield, ExternalLink, Search, GitBranch, Container,
  Bell, Cpu, Eye, Code2, KeyRound, Play, AlertTriangle
} from 'lucide-react';
import api from '../../services/api';

// ─── Integration Definitions ─────────────────────────────
const SOURCE_CONTROL = [
  { key: 'azure_repos', name: 'Azure Repos', icon: '/', color: '#0078D4',
    fields: [
      { id: 'org_url', label: 'Organization URL', placeholder: 'https://dev.azure.com/your-org' },
      { id: 'pat', label: 'Personal Access Token', placeholder: 'Your Azure DevOps PAT', type: 'password' },
      { id: 'project', label: 'Default Project', placeholder: 'Project name' },
    ]},
  { key: 'bitbucket', name: 'Bitbucket Cloud', icon: 'B', color: '#0052CC',
    fields: [
      { id: 'workspace', label: 'Workspace', placeholder: 'your-workspace' },
      { id: 'username', label: 'Username', placeholder: 'Bitbucket username' },
      { id: 'app_password', label: 'App Password', placeholder: 'Bitbucket app password', type: 'password' },
    ]},
  { key: 'bitbucket_app', name: 'Bitbucket Cloud App', icon: 'B', color: '#0052CC',
    fields: [
      { id: 'client_id', label: 'Client ID', placeholder: 'OAuth consumer key' },
      { id: 'client_secret', label: 'Client Secret', placeholder: 'OAuth consumer secret', type: 'password' },
    ]},
  { key: 'github', name: 'GitHub', icon: 'G', color: '#333',
    fields: [
      { id: 'token', label: 'Personal Access Token', placeholder: 'ghp_xxxxxxxxxxxx', type: 'password' },
      { id: 'enterprise_url', label: 'Enterprise URL (optional)', placeholder: 'https://api.github.com' },
    ]},
  { key: 'gitlab', name: 'GitLab', icon: 'L', color: '#FC6D26',
    fields: [
      { id: 'url', label: 'GitLab URL', placeholder: 'https://gitlab.com' },
      { id: 'token', label: 'Personal Access Token', placeholder: 'glpat-xxxxxxxxxxxx', type: 'password' },
    ]},
];

const CONTAINER_REGISTRIES = [
  { key: 'acr', name: 'ACR', fullName: 'Azure Container Registry', color: '#0078D4',
    fields: [
      { id: 'registry_url', label: 'Registry URL', placeholder: 'yourregistry.azurecr.io' },
      { id: 'username', label: 'Username', placeholder: 'Service principal ID' },
      { id: 'password', label: 'Password', placeholder: 'Service principal password', type: 'password' },
    ]},
  { key: 'digitalocean', name: 'DigitalOcean', fullName: 'DigitalOcean Container Registry', color: '#0080FF',
    fields: [
      { id: 'api_token', label: 'API Token', placeholder: 'DigitalOcean API token', type: 'password' },
      { id: 'registry_name', label: 'Registry Name', placeholder: 'your-registry' },
    ]},
  { key: 'docker_hub', name: 'Docker Hub', fullName: 'Docker Hub', color: '#2496ED',
    fields: [
      { id: 'username', label: 'Username', placeholder: 'Docker Hub username' },
      { id: 'access_token', label: 'Access Token', placeholder: 'Docker Hub access token', type: 'password' },
    ]},
  { key: 'ecr', name: 'ECR', fullName: 'AWS ECR', color: '#FF9900',
    fields: [
      { id: 'region', label: 'AWS Region', placeholder: 'us-east-1' },
      { id: 'access_key', label: 'Access Key ID', placeholder: 'AKIA...' },
      { id: 'secret_key', label: 'Secret Access Key', placeholder: 'AWS secret key', type: 'password' },
    ]},
  { key: 'gcr', name: 'GCR', fullName: 'Google Container Registry', color: '#4285F4',
    fields: [
      { id: 'project_id', label: 'GCP Project ID', placeholder: 'your-project-id' },
      { id: 'service_account_key', label: 'Service Account Key (JSON)', placeholder: '{"type":"service_account"...}', type: 'password' },
    ]},
  { key: 'github_cr', name: 'GitHub Container Registry', fullName: 'GitHub Container Registry', color: '#333',
    fields: [
      { id: 'token', label: 'Personal Access Token', placeholder: 'ghp_xxxxxxxxxxxx', type: 'password' },
      { id: 'owner', label: 'Owner/Org', placeholder: 'github-username-or-org' },
    ]},
  { key: 'gitlab_cr', name: 'GitLab Container Registry', fullName: 'GitLab Container Registry', color: '#FC6D26',
    fields: [
      { id: 'url', label: 'GitLab URL', placeholder: 'https://registry.gitlab.com' },
      { id: 'token', label: 'Access Token', placeholder: 'Registry access token', type: 'password' },
    ]},
  { key: 'google_artifact', name: 'Google Artifact Registry', fullName: 'Google Artifact Registry', color: '#4285F4',
    fields: [
      { id: 'project_id', label: 'GCP Project ID', placeholder: 'your-project-id' },
      { id: 'location', label: 'Location', placeholder: 'us-central1' },
      { id: 'service_account_key', label: 'Service Account Key', placeholder: 'JSON key', type: 'password' },
    ]},
  { key: 'quay', name: 'Quay', fullName: 'Red Hat Quay', color: '#EE0000',
    fields: [
      { id: 'url', label: 'Quay URL', placeholder: 'https://quay.io' },
      { id: 'token', label: 'OAuth Token', placeholder: 'Quay access token', type: 'password' },
    ]},
];

const CI_CD = [
  { key: 'terraform_cloud', name: 'Terraform Cloud', color: '#7B42BC',
    fields: [
      { id: 'org', label: 'Organization', placeholder: 'your-terraform-org' },
      { id: 'token', label: 'API Token', placeholder: 'Terraform Cloud API token', type: 'password' },
    ]},
  { key: 'jenkins', name: 'Jenkins', color: '#D24939',
    fields: [
      { id: 'url', label: 'Jenkins URL', placeholder: 'https://jenkins.example.com' },
      { id: 'username', label: 'Username', placeholder: 'Jenkins admin user' },
      { id: 'api_token', label: 'API Token', placeholder: 'Jenkins API token', type: 'password' },
    ]},
];

const NOTIFICATIONS = [
  { key: 'slack', name: 'Slack', color: '#4A154B',
    fields: [
      { id: 'webhook_url', label: 'Webhook URL', placeholder: 'https://hooks.slack.com/services/...', type: 'password' },
      { id: 'channel', label: 'Default Channel', placeholder: '#security-alerts' },
    ]},
];

// ─── Component ─────────────────────────────
const IntegrationsPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const proBasePath = location.pathname.startsWith('/litesabre-pro') ? '/litesabre-pro' : '/grc';

  const [configs, setConfigs] = useState({});
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('source_control');

  // Config dialog
  const [editIntegration, setEditIntegration] = useState(null);
  const [formValues, setFormValues] = useState({});
  const [saving, setSaving] = useState(false);

  // Code scan dialog
  const [showCodeScan, setShowCodeScan] = useState(false);
  const [scanOwner, setScanOwner] = useState('');
  const [scanRepo, setScanRepo] = useState('');
  const [scanType, setScanType] = useState('both');
  const [scanning, setScanning] = useState(false);
  const [scanResult, setScanResult] = useState(null);

  // Webhook config
  const [showWebhookConfig, setShowWebhookConfig] = useState(false);
  const [webhookConfig, setWebhookConfig] = useState(null);
  const [webhookEnabled, setWebhookEnabled] = useState(true);
  const [webhookScanType, setWebhookScanType] = useState('both');
  const [webhookSlackNotify, setWebhookSlackNotify] = useState(true);
  const [webhookBranches, setWebhookBranches] = useState('main, master, develop');
  const [webhookEvents, setWebhookEvents] = useState([]);
  const [savingWebhook, setSavingWebhook] = useState(false);

  // Also fetch legacy github/jira status
  const [githubStatus, setGithubStatus] = useState(null);
  const [jiraStatus, setJiraStatus] = useState(null);

  useEffect(() => { loadConfigs(); }, []);

  const loadConfigs = async () => {
    setLoading(true);
    try {
      const [cfgRes, ghRes, jiraRes, whRes, whEvtRes] = await Promise.all([
        api.get('/integrations/configs').catch(() => ({ data: { configs: {} } })),
        api.get('/integrations/github/status').catch(() => ({ data: { connected: false } })),
        api.get('/integrations/jira/status').catch(() => ({ data: { connected: false } })),
        api.get('/integrations/github/webhook/config').catch(() => ({ data: { configured: false } })),
        api.get('/integrations/github/webhook/events').catch(() => ({ data: { events: [] } })),
      ]);
      setConfigs(cfgRes.data.configs || {});
      setGithubStatus(ghRes.data);
      setJiraStatus(jiraRes.data);
      if (whRes.data.configured) {
        setWebhookConfig(whRes.data);
        setWebhookEnabled(whRes.data.config?.enabled ?? true);
        setWebhookScanType(whRes.data.config?.scan_type || 'both');
        setWebhookSlackNotify(whRes.data.config?.slack_notify ?? true);
        setWebhookBranches((whRes.data.config?.branches || ['main', 'master']).join(', '));
      }
      setWebhookEvents(whEvtRes.data.events || []);
    } catch {
      toast.error('Failed to load integrations');
    } finally { setLoading(false); }
  };

  const isConfigured = (key) => {
    if (key === 'github') return githubStatus?.connected;
    if (key === 'jira') return jiraStatus?.connected;
    return !!configs[key]?.configured;
  };

  const openEditDialog = (integration) => {
    setEditIntegration(integration);
    const existing = configs[integration.key]?.config || {};
    const vals = {};
    integration.fields.forEach(f => { vals[f.id] = existing[f.id] || ''; });
    setFormValues(vals);
  };

  const handleSave = async () => {
    if (!editIntegration) return;
    setSaving(true);
    try {
      // For github/jira, use their dedicated endpoints
      if (editIntegration.key === 'github') {
        await api.post('/integrations/github/configure', {
          token: formValues.token,
          enterprise_url: formValues.enterprise_url || 'https://api.github.com',
        });
        toast.success('GitHub configured');
      } else if (editIntegration.key === 'jira') {
        // jira handled in settings
        toast.info('Jira configuration is in Settings > Integrations');
      } else {
        await api.post('/integrations/configs', {
          integration_type: editIntegration.key,
          config: formValues,
        });
        toast.success(`${editIntegration.name} configuration saved`);
      }
      setEditIntegration(null);
      loadConfigs();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save');
    } finally { setSaving(false); }
  };

  const handleDisconnect = async (key) => {
    try {
      await api.delete(`/integrations/configs/${key}`);
      toast.success('Integration disconnected');
      loadConfigs();
    } catch { toast.error('Failed to disconnect'); }
  };

  const handleCodeScan = async () => {
    if (!scanOwner || !scanRepo) return toast.error('Enter owner and repo');
    setScanning(true);
    setScanResult(null);
    try {
      const res = await api.post('/integrations/github/code-scan', {
        owner: scanOwner, repo: scanRepo, scan_type: scanType,
      });
      setScanResult(res.data);
      toast.success(`Scan complete: ${res.data.total_findings} findings`);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Scan failed');
    } finally { setScanning(false); }
  };

  const handleSaveWebhook = async () => {
    setSavingWebhook(true);
    try {
      const res = await api.post('/integrations/github/webhook/configure', {
        enabled: webhookEnabled,
        scan_type: webhookScanType,
        slack_notify: webhookSlackNotify,
        branches: webhookBranches.split(',').map(b => b.trim()).filter(Boolean),
      });
      setWebhookConfig(res.data);
      toast.success('Webhook configuration saved');
      setShowWebhookConfig(false);
      loadConfigs();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save webhook config');
    } finally { setSavingWebhook(false); }
  };

  const IntegrationCard = ({ integration, showDisconnect = true }) => {
    const configured = isConfigured(integration.key);
    return (
      <div
        className="flex items-center justify-between p-3 rounded-lg border hover:border-primary/30 transition-colors"
        data-testid={`integration-${integration.key}`}
      >
        <div className="flex items-center gap-3">
          <div
            className="w-9 h-9 rounded-md flex items-center justify-center text-white font-bold text-sm shrink-0"
            style={{ backgroundColor: integration.color }}
          >
            {integration.icon || integration.name.charAt(0)}
          </div>
          <div>
            <p className="font-medium text-sm">{integration.fullName || integration.name}</p>
            <p className="text-xs text-muted-foreground">
              {configured ? 'Configured' : 'Not configured'}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {configured ? (
            <Badge variant="secondary" className="bg-emerald-100 text-emerald-700 text-[10px]">
              <CheckCircle2 className="h-3 w-3 mr-1" /> Connected
            </Badge>
          ) : (
            <Badge variant="secondary" className="text-[10px]">
              <XCircle className="h-3 w-3 mr-1" /> Not configured
            </Badge>
          )}
          <Button
            variant="ghost"
            size="sm"
            className="text-xs"
            onClick={() => openEditDialog(integration)}
            data-testid={`edit-${integration.key}`}
          >
            <Settings className="h-3.5 w-3.5 mr-1" />
            {configured ? 'Edit' : 'Configure'}
          </Button>
        </div>
      </div>
    );
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6" data-testid="integrations-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Button variant="ghost" size="icon" onClick={() => navigate(proBasePath)} data-testid="back-btn">
            <ArrowLeft className="h-5 w-5" />
          </Button>
          <div>
            <h1 className="text-2xl font-bold tracking-tight">Integrations</h1>
            <p className="text-sm text-muted-foreground">
              Connect your source control, registries, CI/CD pipelines, and notifications
            </p>
          </div>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" size="sm" onClick={loadConfigs} data-testid="refresh-integrations">
            <RefreshCw className="h-4 w-4 mr-1" /> Refresh
          </Button>
          {githubStatus?.connected && (
            <Button size="sm" onClick={() => setShowCodeScan(true)} data-testid="code-scan-btn">
              <Code2 className="h-4 w-4 mr-1" /> Code & Secrets Scan
            </Button>
          )}
        </div>
      </div>

      {/* Summary cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        {[
          { label: 'Source Control', count: SOURCE_CONTROL.filter(i => isConfigured(i.key)).length, total: SOURCE_CONTROL.length, icon: FolderGit2, color: 'text-blue-600' },
          { label: 'Container Registries', count: CONTAINER_REGISTRIES.filter(i => isConfigured(i.key)).length, total: CONTAINER_REGISTRIES.length, icon: Container, color: 'text-purple-600' },
          { label: 'CI/CD', count: CI_CD.filter(i => isConfigured(i.key)).length, total: CI_CD.length, icon: GitBranch, color: 'text-amber-600' },
          { label: 'Notifications', count: NOTIFICATIONS.filter(i => isConfigured(i.key)).length, total: NOTIFICATIONS.length, icon: Bell, color: 'text-emerald-600' },
        ].map(s => (
          <Card key={s.label}>
            <CardContent className="p-4 flex items-center gap-3">
              <s.icon className={`h-5 w-5 ${s.color}`} />
              <div>
                <p className="text-xs text-muted-foreground">{s.label}</p>
                <p className="text-lg font-bold">{s.count}<span className="text-sm font-normal text-muted-foreground">/{s.total}</span></p>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList>
          <TabsTrigger value="source_control" data-testid="tab-scm"><FolderGit2 className="h-4 w-4 mr-1" /> Source Control</TabsTrigger>
          <TabsTrigger value="registries" data-testid="tab-registries"><Container className="h-4 w-4 mr-1" /> Container Registries</TabsTrigger>
          <TabsTrigger value="cicd" data-testid="tab-cicd"><GitBranch className="h-4 w-4 mr-1" /> CI/CD</TabsTrigger>
          <TabsTrigger value="notifications" data-testid="tab-notifications"><Bell className="h-4 w-4 mr-1" /> Notifications</TabsTrigger>
          <TabsTrigger value="webhooks" data-testid="tab-webhooks"><Cpu className="h-4 w-4 mr-1" /> Webhooks</TabsTrigger>
        </TabsList>

        <TabsContent value="source_control" className="mt-4">
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-base">Source Control Management</CardTitle>
              <CardDescription>Connect your SCM providers to enable repository scanning</CardDescription>
            </CardHeader>
            <CardContent className="space-y-2">
              {SOURCE_CONTROL.map(i => <IntegrationCard key={i.key} integration={i} />)}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="registries" className="mt-4">
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-base">Container Registries</CardTitle>
              <CardDescription>Connect container registries for image scanning</CardDescription>
            </CardHeader>
            <CardContent className="space-y-2">
              {CONTAINER_REGISTRIES.map(i => <IntegrationCard key={i.key} integration={i} />)}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="cicd" className="mt-4">
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-base">Continuous Integration / Continuous Deployment</CardTitle>
              <CardDescription>Connect CI/CD tools for pipeline security</CardDescription>
            </CardHeader>
            <CardContent className="space-y-2">
              {CI_CD.map(i => <IntegrationCard key={i.key} integration={i} />)}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="notifications" className="mt-4">
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-base">Notifications</CardTitle>
              <CardDescription>Configure notification channels for security alerts</CardDescription>
            </CardHeader>
            <CardContent className="space-y-2">
              {NOTIFICATIONS.map(i => <IntegrationCard key={i.key} integration={i} />)}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Webhooks Tab */}
        <TabsContent value="webhooks" className="mt-4 space-y-4">
          <Card>
            <CardHeader className="pb-3">
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle className="text-base">GitHub Webhook Auto-Scanning</CardTitle>
                  <CardDescription>Automatically trigger SAST + secrets scans on push events and send Slack notifications</CardDescription>
                </div>
                <Button size="sm" onClick={() => setShowWebhookConfig(true)} data-testid="configure-webhook-btn">
                  <Settings className="h-4 w-4 mr-1" /> {webhookConfig?.configured ? 'Edit' : 'Configure'}
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              {webhookConfig?.configured ? (
                <div className="space-y-4">
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                    <div className="p-3 border rounded-lg">
                      <p className="text-xs text-muted-foreground">Status</p>
                      <Badge className={webhookEnabled ? 'bg-emerald-100 text-emerald-700' : 'bg-gray-100 text-gray-600'}>
                        {webhookEnabled ? 'Active' : 'Disabled'}
                      </Badge>
                    </div>
                    <div className="p-3 border rounded-lg">
                      <p className="text-xs text-muted-foreground">Scan Type</p>
                      <p className="text-sm font-medium capitalize">{webhookScanType === 'both' ? 'SAST + Secrets' : webhookScanType.toUpperCase()}</p>
                    </div>
                    <div className="p-3 border rounded-lg">
                      <p className="text-xs text-muted-foreground">Slack Notify</p>
                      <Badge className={webhookSlackNotify ? 'bg-blue-100 text-blue-700' : 'bg-gray-100 text-gray-600'}>
                        {webhookSlackNotify ? 'Enabled' : 'Disabled'}
                      </Badge>
                    </div>
                    <div className="p-3 border rounded-lg">
                      <p className="text-xs text-muted-foreground">Branches</p>
                      <p className="text-sm font-medium">{webhookBranches}</p>
                    </div>
                  </div>
                  <div className="bg-muted/50 rounded-lg p-3">
                    <p className="text-xs font-medium mb-1">Webhook URL (add this in your GitHub repo settings):</p>
                    <code className="text-xs bg-background px-2 py-1 rounded border block overflow-x-auto" data-testid="webhook-url">
                      {window.location.origin}/api/integrations{webhookConfig.webhook_url}
                    </code>
                    <p className="text-xs text-muted-foreground mt-2">
                      <strong>Secret:</strong> <code className="bg-background px-1 rounded">{webhookConfig.webhook_secret}</code>
                    </p>
                    <p className="text-xs text-muted-foreground mt-1">
                      In GitHub: Settings &gt; Webhooks &gt; Add webhook &gt; Payload URL: above URL, Content type: application/json, Events: Just the push event
                    </p>
                  </div>
                </div>
              ) : (
                <div className="text-center py-8 text-muted-foreground">
                  <Cpu className="h-10 w-10 mx-auto mb-3 opacity-40" />
                  <p>No webhook configured. Click "Configure" to set up auto-scanning on push events.</p>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Recent Webhook Events */}
          {webhookEvents.length > 0 && (
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-base">Recent Webhook Events</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="border rounded-lg overflow-hidden">
                  <table className="w-full text-sm">
                    <thead className="bg-muted/50">
                      <tr>
                        <th className="text-left p-3 font-medium">Repository</th>
                        <th className="text-left p-3 font-medium">Branch</th>
                        <th className="text-left p-3 font-medium">Pusher</th>
                        <th className="text-left p-3 font-medium">Status</th>
                        <th className="text-left p-3 font-medium">Findings</th>
                        <th className="text-left p-3 font-medium">Time</th>
                      </tr>
                    </thead>
                    <tbody>
                      {webhookEvents.map(ev => (
                        <tr key={ev.id} className="border-t hover:bg-muted/30" data-testid={`webhook-event-${ev.id}`}>
                          <td className="p-3 font-medium">{ev.repository}</td>
                          <td className="p-3"><Badge variant="outline" className="text-[10px]">{ev.branch}</Badge></td>
                          <td className="p-3">{ev.pusher}</td>
                          <td className="p-3">
                            <Badge className={
                              ev.status === 'completed' ? 'bg-emerald-100 text-emerald-700' :
                              ev.status === 'scanning' ? 'bg-blue-100 text-blue-700' :
                              'bg-red-100 text-red-700'
                            }>{ev.status}</Badge>
                          </td>
                          <td className="p-3">
                            {ev.total_findings != null ? (
                              <span>{ev.severity_counts?.critical > 0 && <Badge className="bg-red-600 text-white text-[10px] mr-1">{ev.severity_counts.critical}C</Badge>}
                              {ev.severity_counts?.high > 0 && <Badge className="bg-orange-500 text-white text-[10px] mr-1">{ev.severity_counts.high}H</Badge>}
                              {ev.total_findings} total</span>
                            ) : '-'}
                          </td>
                          <td className="p-3 text-xs text-muted-foreground">{new Date(ev.created_at).toLocaleString()}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </CardContent>
            </Card>
          )}
        </TabsContent>
      </Tabs>

      {/* Edit Integration Dialog */}
      <Dialog open={!!editIntegration} onOpenChange={v => { if (!v) setEditIntegration(null); }}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              {editIntegration && (
                <div
                  className="w-7 h-7 rounded flex items-center justify-center text-white text-xs font-bold"
                  style={{ backgroundColor: editIntegration?.color }}
                >
                  {editIntegration?.icon || editIntegration?.name?.charAt(0)}
                </div>
              )}
              Configure {editIntegration?.name}
            </DialogTitle>
            <DialogDescription>Enter credentials to connect this integration</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-2">
            {editIntegration?.fields?.map(f => (
              <div key={f.id} className="space-y-1.5">
                <Label htmlFor={f.id}>{f.label}</Label>
                <Input
                  id={f.id}
                  type={f.type || 'text'}
                  placeholder={f.placeholder}
                  value={formValues[f.id] || ''}
                  onChange={e => setFormValues(prev => ({ ...prev, [f.id]: e.target.value }))}
                  data-testid={`config-field-${f.id}`}
                />
              </div>
            ))}
          </div>
          <DialogFooter className="flex gap-2">
            {isConfigured(editIntegration?.key) && editIntegration?.key !== 'github' && (
              <Button variant="destructive" size="sm" onClick={() => { handleDisconnect(editIntegration.key); setEditIntegration(null); }}>
                Disconnect
              </Button>
            )}
            <div className="flex-1" />
            <Button variant="outline" onClick={() => setEditIntegration(null)}>Cancel</Button>
            <Button onClick={handleSave} disabled={saving} data-testid="save-config-btn">
              {saving ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : <CheckCircle2 className="h-4 w-4 mr-1" />}
              Save
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Code & Secrets Scan Dialog */}
      <Dialog open={showCodeScan} onOpenChange={v => { if (!v) { setShowCodeScan(false); setScanResult(null); } else setShowCodeScan(true); }}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Shield className="h-5 w-5" /> Code & Secrets Scanner
            </DialogTitle>
            <DialogDescription>
              Scan a GitHub repository for security vulnerabilities and exposed secrets
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Repository Owner</Label>
                <Input
                  placeholder="owner / org"
                  value={scanOwner}
                  onChange={e => setScanOwner(e.target.value)}
                  data-testid="scan-owner-input"
                />
              </div>
              <div>
                <Label>Repository Name</Label>
                <Input
                  placeholder="repo-name"
                  value={scanRepo}
                  onChange={e => setScanRepo(e.target.value)}
                  data-testid="scan-repo-input"
                />
              </div>
            </div>
            <div>
              <Label>Scan Type</Label>
              <Select value={scanType} onValueChange={setScanType}>
                <SelectTrigger data-testid="scan-type-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="both">SAST + Secrets (Full)</SelectItem>
                  <SelectItem value="sast">Code Security (SAST Only)</SelectItem>
                  <SelectItem value="secrets">Secrets Detection Only</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <Button onClick={handleCodeScan} disabled={scanning} className="w-full" data-testid="run-scan-btn">
              {scanning ? (
                <><Loader2 className="h-4 w-4 animate-spin mr-2" /> Scanning repository...</>
              ) : (
                <><Play className="h-4 w-4 mr-2" /> Run Scan</>
              )}
            </Button>

            {/* Scan Results */}
            {scanResult && (
              <div className="space-y-3 border-t pt-4">
                <div className="flex items-center justify-between">
                  <h3 className="font-semibold">Scan Results</h3>
                  <Badge variant="outline">{scanResult.files_scanned} files scanned</Badge>
                </div>
                <div className="grid grid-cols-5 gap-2">
                  {[
                    { label: 'Critical', count: scanResult.severity_counts?.critical || 0, color: 'bg-red-600 text-white' },
                    { label: 'High', count: scanResult.severity_counts?.high || 0, color: 'bg-orange-500 text-white' },
                    { label: 'Medium', count: scanResult.severity_counts?.medium || 0, color: 'bg-yellow-500 text-black' },
                    { label: 'Low', count: scanResult.severity_counts?.low || 0, color: 'bg-blue-500 text-white' },
                    { label: 'Info', count: scanResult.severity_counts?.info || 0, color: 'bg-gray-500 text-white' },
                  ].map(s => (
                    <div key={s.label} className="text-center p-2 rounded-md border">
                      <div className={`text-xl font-bold ${s.count > 0 ? '' : 'text-muted-foreground'}`}>{s.count}</div>
                      <Badge className={`text-[10px] ${s.count > 0 ? s.color : ''}`}>{s.label}</Badge>
                    </div>
                  ))}
                </div>
                {scanResult.findings?.length > 0 && (
                  <ScrollArea className="h-60 border rounded-md">
                    <div className="divide-y">
                      {scanResult.findings.slice(0, 30).map((f, i) => (
                        <div key={i} className="p-2.5 text-sm hover:bg-muted/30">
                          <div className="flex items-center gap-2">
                            <Badge className={`text-[10px] ${
                              f.severity === 'critical' ? 'bg-red-600 text-white' :
                              f.severity === 'high' ? 'bg-orange-500 text-white' :
                              f.severity === 'medium' ? 'bg-yellow-500 text-black' :
                              'bg-blue-500 text-white'
                            }`}>{f.severity}</Badge>
                            <span className="font-medium truncate">{f.title || f.rule_id || 'Finding'}</span>
                          </div>
                          <p className="text-xs text-muted-foreground mt-0.5 truncate">
                            {f.file_path && <span className="font-mono">{f.file_path}</span>}
                            {f.line_number && <span> :L{f.line_number}</span>}
                            {f.description && <span> - {f.description}</span>}
                          </p>
                        </div>
                      ))}
                    </div>
                  </ScrollArea>
                )}
                {scanResult.total_findings === 0 && (
                  <div className="text-center py-6 text-emerald-600">
                    <CheckCircle2 className="h-8 w-8 mx-auto mb-2" />
                    <p className="font-medium">No security issues found</p>
                  </div>
                )}
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>
      {/* Webhook Configuration Dialog */}
      <Dialog open={showWebhookConfig} onOpenChange={v => { if (!v) setShowWebhookConfig(false); else setShowWebhookConfig(true); }}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Cpu className="h-5 w-5" /> Configure Webhook Auto-Scanning
            </DialogTitle>
            <DialogDescription>
              Set up automatic scanning when code is pushed to your GitHub repositories
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div className="flex items-center justify-between">
              <Label>Enable Webhook</Label>
              <Button
                variant={webhookEnabled ? 'default' : 'outline'}
                size="sm"
                onClick={() => setWebhookEnabled(!webhookEnabled)}
                data-testid="webhook-enabled-toggle"
              >
                {webhookEnabled ? 'Enabled' : 'Disabled'}
              </Button>
            </div>
            <div className="space-y-1.5">
              <Label>Scan Type</Label>
              <Select value={webhookScanType} onValueChange={setWebhookScanType}>
                <SelectTrigger data-testid="webhook-scan-type">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="both">SAST + Secrets (Full)</SelectItem>
                  <SelectItem value="sast">Code Security (SAST Only)</SelectItem>
                  <SelectItem value="secrets">Secrets Detection Only</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label>Monitored Branches (comma-separated)</Label>
              <Input
                value={webhookBranches}
                onChange={e => setWebhookBranches(e.target.value)}
                placeholder="main, master, develop"
                data-testid="webhook-branches-input"
              />
            </div>
            <div className="flex items-center justify-between">
              <div>
                <Label>Slack Notifications</Label>
                <p className="text-xs text-muted-foreground">Send alerts for critical/high findings</p>
              </div>
              <Button
                variant={webhookSlackNotify ? 'default' : 'outline'}
                size="sm"
                onClick={() => setWebhookSlackNotify(!webhookSlackNotify)}
                data-testid="webhook-slack-toggle"
              >
                {webhookSlackNotify ? 'Enabled' : 'Disabled'}
              </Button>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowWebhookConfig(false)}>Cancel</Button>
            <Button onClick={handleSaveWebhook} disabled={savingWebhook} data-testid="save-webhook-btn">
              {savingWebhook ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : <CheckCircle2 className="h-4 w-4 mr-1" />}
              Save Configuration
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default IntegrationsPage;
